import { useState, useEffect } from "react";
import { getFarmers, getMilkEntries } from "../services/firebaseService";
import { FaCalendarAlt, FaFilter, FaChartBar, FaSun, FaMoon, FaUsers } from 'react-icons/fa';

export default function SummaryReport() {
  const [entries, setEntries] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [filtered, setFiltered] = useState([]);
  const [reportType, setReportType] = useState("custom"); // "custom" or "weekly"
  const [selectedWeekStart, setSelectedWeekStart] = useState("");

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [farmersData, entriesData] = await Promise.all([getFarmers(), getMilkEntries()]);
      setFarmers(farmersData);
      setEntries(entriesData);
      setFiltered(entriesData);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    if (!fromDate || !toDate || fromDate > toDate) return;
    setFiltered(entries.filter(e => e.date >= fromDate && e.date <= toDate));
  };

  const handleWeeklyFilter = () => {
    if (!selectedWeekStart) return;
    const selected = new Date(selectedWeekStart);
    const dayOfWeek = selected.getDay();
    const diff = selected.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(selected.setDate(diff));
    const mondayStr = monday.toISOString().slice(0, 10);
    const end = new Date(monday);
    end.setDate(end.getDate() + 6);
    const endStr = end.toISOString().slice(0, 10);
    setFromDate(mondayStr);
    setToDate(endStr);
    setSelectedWeekStart(mondayStr);
    setFiltered(entries.filter(e => e.date >= mondayStr && e.date <= endStr));
  };

  const getWeekDays = (startDate) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      days.push(d.toISOString().slice(0, 10));
    }
    return days;
  };

  const getWeeklyDayBreakdown = () => {
    if (reportType !== "weekly" || !selectedWeekStart) return [];
    const weekDays = getWeekDays(selectedWeekStart);
    return weekDays.map(day => {
      const dayEntries = filtered.filter(e => e.date === day);
      const dayTotal = dayEntries.reduce((sum, e) => sum + e.quantity, 0);
      const dayAmount = dayEntries.reduce((sum, e) => sum + (e.quantity * e.price), 0);
      return { date: day, quantity: dayTotal, amount: dayAmount, entries: dayEntries.length };
    });
  };

  const getWeeklyFarmerSummary = () => {
    if (reportType !== "weekly" || !selectedWeekStart) return [];
    const farmerData = {};
    filtered.forEach(entry => {
      const farmer = farmers.find(f => f.code === entry.farmerCode);
      const farmerName = farmer ? farmer.name : entry.farmerCode;
      if (!farmerData[entry.farmerCode]) {
        farmerData[entry.farmerCode] = { code: entry.farmerCode, name: farmerName, totalQuantity: 0, totalAmount: 0, cowQuantity: 0, buffaloQuantity: 0, morningQuantity: 0, eveningQuantity: 0 };
      }
      const data = farmerData[entry.farmerCode];
      data.totalQuantity += entry.quantity;
      data.totalAmount += entry.quantity * entry.price;
      if (entry.animalType === "Cow") data.cowQuantity += entry.quantity;
      else if (entry.animalType === "Buffalo") data.buffaloQuantity += entry.quantity;
      if (entry.time === "Morning") data.morningQuantity += entry.quantity;
      else if (entry.time === "Evening") data.eveningQuantity += entry.quantity;
    });
    return Object.values(farmerData).sort((a, b) => a.name.localeCompare(b.name));
  };

  const handlePrintWeekly = () => {
    const weekStart = new Date(selectedWeekStart);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const weekRange = `${weekStart.toLocaleDateString('en-IN')} to ${weekEnd.toLocaleDateString('en-IN')}`;
    
    const dayBreakdown = getWeeklyDayBreakdown();
    const weeklyFarmers = getWeeklyFarmerSummary();
    const weeklyTotalQty = dayBreakdown.reduce((sum, day) => sum + day.quantity, 0);
    const weeklyTotalAmount = dayBreakdown.reduce((sum, day) => sum + day.amount, 0);

    let farmerDetailsHTML = weeklyFarmers.map(farmer => {
      const farmerEntries = filtered.filter(e => e.farmerCode === farmer.code);
      const dayWiseEntries = {};
      dayBreakdown.forEach(day => {
        dayWiseEntries[day.date] = farmerEntries.filter(e => e.date === day.date);
      });
      
      let entriesHTML = dayBreakdown.map(day => {
        const dayEntries = dayWiseEntries[day.date];
        if (dayEntries.length === 0) return '';
        return dayEntries.map((entry, idx) => `
          <tr style="background-color: ${idx % 2 === 0 ? '#fff' : '#f9f9f9'};">
            <td style="border: 1px solid #ddd; padding: 8px;">${idx === 0 ? new Date(day.date).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' }) : ''}</td>
            <td style="border: 1px solid #ddd; padding: 8px;">${entry.animalType}</td>
            <td style="border: 1px solid #ddd; padding: 8px;">${entry.time}</td>
            <td style="border: 1px solid #ddd; padding: 8px;">${entry.quantity.toFixed(1)}</td>
            <td style="border: 1px solid #ddd; padding: 8px;">${entry.price.toFixed(2)}</td>
            <td style="border: 1px solid #ddd; padding: 8px;">${(entry.quantity * entry.price).toFixed(2)}</td>
          </tr>
        `).join('');
      }).join('');

      return `
        <div style="page-break-inside: avoid; margin: 20px 0; padding: 15px; border: 1px solid #ddd; border-radius: 5px;">
          <div style="background-color: #f0f9ff; padding: 10px; border-radius: 3px; margin-bottom: 10px;">
            <strong style="color: #2ecc71; font-size: 14px;">${farmer.name} (${farmer.code})</strong> - Total: ${farmer.totalQuantity.toFixed(1)} L | ₹${farmer.totalAmount.toFixed(2)}
          </div>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background-color: #2ecc71; color: white;">
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Date</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Animal</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Time</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Quantity (L)</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Price (₹/L)</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Total (₹)</th>
              </tr>
            </thead>
            <tbody>
              ${entriesHTML}
            </tbody>
          </table>
        </div>
      `;
    }).join('');

    let printContent = `
      <html>
        <head>
          <title>Weekly Report - ${weekRange}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { text-align: center; color: #2ecc71; }
            h2 { color: #333; margin-top: 20px; border-bottom: 2px solid #2ecc71; padding-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin: 15px 0; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            th { background-color: #2ecc71; color: white; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .total-row { background-color: #e8f5e9; font-weight: bold; }
            .day-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 10px; margin: 15px 0; }
            .day-card { border: 1px solid #ddd; padding: 10px; text-align: center; border-radius: 5px; }
            .day-card strong { color: #2ecc71; font-size: 16px; }
            .summary-box { background-color: #f0f9ff; border: 2px solid #2ecc71; padding: 15px; text-align: center; margin: 15px 0; border-radius: 5px; }
            .summary-box h3 { margin: 0; color: #2ecc71; }
            .summary-box .value { font-size: 24px; font-weight: bold; color: #2ecc71; }
            .summary-box .amount { font-size: 16px; color: #555; }
          </style>
        </head>
        <body>
          <h1>🐄 Dairy ERP System - Weekly Report</h1>
          <p style="text-align: center; font-size: 16px;"><strong>Week: ${weekRange}</strong></p>
          
          <h2>📈 Daily Breakdown</h2>
          <div class="day-grid">
            ${dayBreakdown.map(day => `
              <div class="day-card">
                <div>${new Date(day.date).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                <strong>${day.quantity.toFixed(1)} L</strong>
                <div>₹${day.amount.toFixed(0)}</div>
                <div style="font-size: 12px; color: #999;">${day.entries} entries</div>
              </div>
            `).join('')}
          </div>

          <div class="summary-box">
            <h3>Weekly Total</h3>
            <div class="value">${weeklyTotalQty.toFixed(1)} L</div>
            <div class="amount">₹${weeklyTotalAmount.toFixed(0)}</div>
          </div>

          <h2>👨‍🌾 Farmer-wise Summary</h2>
          <table>
            <thead>
              <tr>
                <th>Farmer</th>
                <th>Total Qty (L)</th>
                <th>Total Amount (₹)</th>
                <th>🐄 Cow (L)</th>
                <th>🦬 Buffalo (L)</th>
                <th>🌅 Morning (L)</th>
                <th>🌙 Evening (L)</th>
              </tr>
            </thead>
            <tbody>
              ${weeklyFarmers.map(farmer => `
                <tr>
                  <td><strong>${farmer.name}</strong><br/><small>${farmer.code}</small></td>
                  <td>${farmer.totalQuantity.toFixed(1)}</td>
                  <td>₹${farmer.totalAmount.toFixed(2)}</td>
                  <td>${farmer.cowQuantity.toFixed(1)}</td>
                  <td>${farmer.buffaloQuantity.toFixed(1)}</td>
                  <td>${farmer.morningQuantity.toFixed(1)}</td>
                  <td>${farmer.eveningQuantity.toFixed(1)}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td>WEEKLY TOTAL</td>
                <td>${weeklyFarmers.reduce((sum, f) => sum + f.totalQuantity, 0).toFixed(1)}</td>
                <td>₹${weeklyFarmers.reduce((sum, f) => sum + f.totalAmount, 0).toFixed(2)}</td>
                <td>${weeklyFarmers.reduce((sum, f) => sum + f.cowQuantity, 0).toFixed(1)}</td>
                <td>${weeklyFarmers.reduce((sum, f) => sum + f.buffaloQuantity, 0).toFixed(1)}</td>
                <td>${weeklyFarmers.reduce((sum, f) => sum + f.morningQuantity, 0).toFixed(1)}</td>
                <td>${weeklyFarmers.reduce((sum, f) => sum + f.eveningQuantity, 0).toFixed(1)}</td>
              </tr>
            </tbody>
          </table>

          <h2>📋 Farmer-wise Daily Entry Details</h2>
          ${farmerDetailsHTML}

          <p style="text-align: center; margin-top: 30px; color: #999; font-size: 12px;">
            Generated on ${new Date().toLocaleString('en-IN')}
          </p>
        </body>
      </html>
    `;

    const printWindow = window.open('', '', 'height=800,width=1000');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const clearFilter = () => { setFromDate(""); setToDate(""); setFiltered(entries); };

  useEffect(() => { if (!fromDate || !toDate) setFiltered(entries); }, [entries, fromDate, toDate]);

  function sumBy(animal, time, field) {
    return filtered.filter(e => e.animalType === animal && e.time === time).reduce((sum, e) => sum + (e[field] || 0), 0);
  }

  function totalAmount(animal, time) {
    return filtered.filter(e => e.animalType === animal && e.time === time).reduce((sum, e) => sum + (e.quantity * e.price), 0);
  }

  function getFarmerSummary() {
    const farmerData = {};
    filtered.forEach(entry => {
      const farmer = farmers.find(f => f.code === entry.farmerCode);
      const farmerName = farmer ? farmer.name : entry.farmerCode;
      if (!farmerData[entry.farmerCode]) {
        farmerData[entry.farmerCode] = { code: entry.farmerCode, name: farmerName, totalQuantity: 0, totalAmount: 0, cowQuantity: 0, cowAmount: 0, buffaloQuantity: 0, buffaloAmount: 0, morningQuantity: 0, morningAmount: 0, eveningQuantity: 0, eveningAmount: 0 };
      }
      const data = farmerData[entry.farmerCode];
      data.totalQuantity += entry.quantity;
      data.totalAmount += entry.quantity * entry.price;
      if (entry.animalType === "Cow") { data.cowQuantity += entry.quantity; data.cowAmount += entry.quantity * entry.price; }
      else if (entry.animalType === "Buffalo") { data.buffaloQuantity += entry.quantity; data.buffaloAmount += entry.quantity * entry.price; }
      if (entry.time === "Morning") { data.morningQuantity += entry.quantity; data.morningAmount += entry.quantity * entry.price; }
      else if (entry.time === "Evening") { data.eveningQuantity += entry.quantity; data.eveningAmount += entry.quantity * entry.price; }
    });
    return Object.values(farmerData).sort((a, b) => a.name.localeCompare(b.name));
  }

  const cowMorningQty = sumBy("Cow", "Morning", "quantity");
  const cowEveningQty = sumBy("Cow", "Evening", "quantity");
  const buffaloMorningQty = sumBy("Buffalo", "Morning", "quantity");
  const buffaloEveningQty = sumBy("Buffalo", "Evening", "quantity");
  const totalMilk = cowMorningQty + cowEveningQty + buffaloMorningQty + buffaloEveningQty;
  const totalRevenue = totalAmount("Cow", "Morning") + totalAmount("Cow", "Evening") + totalAmount("Buffalo", "Morning") + totalAmount("Buffalo", "Evening");
  const farmerSummary = getFarmerSummary();

  return (
    <div className="container fade-in">
      <div className="stats-row">
        <div className="stat-card highlight"><div className="stat-icon green"><FaChartBar /></div><div className="stat-info"><span className="stat-number">{totalMilk.toFixed(1)} L</span><span className="stat-label">Total Milk</span></div></div>
        <div className="stat-card"><div className="stat-icon blue"><span>💰</span></div><div className="stat-info"><span className="stat-number">₹{totalRevenue.toFixed(0)}</span><span className="stat-label">Total Revenue</span></div></div>
        <div className="stat-card"><div className="stat-icon orange"><FaUsers /></div><div className="stat-info"><span className="stat-number">{farmerSummary.length}</span><span className="stat-label">Active Farmers</span></div></div>
        <div className="stat-card"><div className="stat-icon purple"><span>📝</span></div><div className="stat-info"><span className="stat-number">{filtered.length}</span><span className="stat-label">Total Entries</span></div></div>
      </div>

      <div className="card">
        <div className="card-header-custom"><div className="card-title-icon"><FaFilter /></div><div><h2>Milk Collection Summary</h2><p>Filter and analyze milk collection data</p></div></div>
        {loading ? (<div className="loading-state"><div className="loader"></div><p>Loading data...</p></div>) : (
          <>
            <div className="filter-bar" style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <button 
                  className={`btn ${reportType === 'custom' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => { setReportType('custom'); setSelectedWeekStart(''); setFromDate(''); setToDate(''); setFiltered(entries); }}
                >
                  📅 Custom Date Range
                </button>
              </div>

              {reportType === 'custom' ? (
                <>
                  <div className="filter-group"><label><FaCalendarAlt /> From Date</label><input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} /></div>
                  <div className="filter-group"><label><FaCalendarAlt /> To Date</label><input type="date" value={toDate} onChange={e => setToDate(e.target.value)} /></div>
                  <div className="filter-actions">
                    <button className="btn btn-primary" onClick={handleFilter} disabled={!fromDate || !toDate || fromDate > toDate}><FaFilter /> Apply Filter</button>
                    {(fromDate || toDate) && (<button className="btn btn-secondary" onClick={clearFilter}>Clear</button>)}
                  </div>
                </>
              ) : (
                <>
                  <div className="filter-group"><label><FaCalendarAlt /> Week Starting</label><input type="date" value={selectedWeekStart} onChange={e => setSelectedWeekStart(e.target.value)} /></div>
                  <div className="filter-actions">
                    <button className="btn btn-primary" onClick={handleWeeklyFilter} disabled={!selectedWeekStart}><FaFilter /> Show Week</button>
                    {selectedWeekStart && (<button className="btn btn-secondary" onClick={() => { setSelectedWeekStart(''); setFromDate(''); setToDate(''); setFiltered(entries); }}>Clear</button>)}
                    {selectedWeekStart && (<button className="btn btn-primary" onClick={handlePrintWeekly} style={{ marginLeft: '10px' }}>🖨️ Print Report</button>)}
                  </div>
                </>
              )}
            </div>

            {reportType === 'weekly' && selectedWeekStart && (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ marginBottom: '15px' }}>📈 Weekly Breakdown</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
                    {getWeeklyDayBreakdown().map((day, idx) => (
                      <div key={idx} style={{ padding: '12px', border: '1px solid #ddd', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>{new Date(day.date).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#2ecc71' }}>{day.quantity.toFixed(1)} L</div>
                        <div style={{ fontSize: '12px', color: '#555' }}>₹{day.amount.toFixed(0)}</div>
                        <div style={{ fontSize: '11px', color: '#999' }}>{day.entries} entries</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: '15px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', textAlign: 'center', border: '2px solid #2ecc71' }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Weekly Total</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2ecc71' }}>{getWeeklyDayBreakdown().reduce((sum, day) => sum + day.quantity, 0).toFixed(1)} L</div>
                    <div style={{ fontSize: '14px', color: '#555' }}>₹{getWeeklyDayBreakdown().reduce((sum, day) => sum + day.amount, 0).toFixed(0)}</div>
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ marginBottom: '15px' }}>👨‍🌾 Weekly Farmer-wise Summary</h3>
                  {getWeeklyFarmerSummary().length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>No farmer data for this week</div>
                  ) : (
                    <div className="table-container">
                      <table className="table enhanced-table">
                        <thead><tr><th>Farmer</th><th>Total Qty</th><th>Total Amount</th><th>🐄 Cow</th><th>🦬 Buffalo</th><th>🌅 Morning</th><th>🌙 Evening</th></tr></thead>
                        <tbody>
                          {getWeeklyFarmerSummary().map((farmer, index) => (
                            <tr key={index}>
                              <td><div className="farmer-cell"><div className="farmer-avatar-small">{farmer.name.charAt(0)}</div><div><strong>{farmer.name}</strong><span className="farmer-code-small">{farmer.code}</span></div></div></td>
                              <td><span className="highlight-value">{farmer.totalQuantity.toFixed(1)} L</span></td>
                              <td><span className="highlight-amount">₹{farmer.totalAmount.toFixed(2)}</span></td>
                              <td>{farmer.cowQuantity.toFixed(1)} L</td>
                              <td>{farmer.buffaloQuantity.toFixed(1)} L</td>
                              <td>{farmer.morningQuantity.toFixed(1)} L</td>
                              <td>{farmer.eveningQuantity.toFixed(1)} L</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td><strong>Weekly Total</strong></td>
                            <td><strong>{getWeeklyFarmerSummary().reduce((sum, f) => sum + f.totalQuantity, 0).toFixed(1)} L</strong></td>
                            <td><strong>₹{getWeeklyFarmerSummary().reduce((sum, f) => sum + f.totalAmount, 0).toFixed(2)}</strong></td>
                            <td><strong>{getWeeklyFarmerSummary().reduce((sum, f) => sum + f.cowQuantity, 0).toFixed(1)} L</strong></td>
                            <td><strong>{getWeeklyFarmerSummary().reduce((sum, f) => sum + f.buffaloQuantity, 0).toFixed(1)} L</strong></td>
                            <td><strong>{getWeeklyFarmerSummary().reduce((sum, f) => sum + f.morningQuantity, 0).toFixed(1)} L</strong></td>
                            <td><strong>{getWeeklyFarmerSummary().reduce((sum, f) => sum + f.eveningQuantity, 0).toFixed(1)} L</strong></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="summary-section">
              <div className="summary-block morning">
                <div className="summary-block-header"><FaSun className="time-icon" /><h3>Morning Collection</h3></div>
                <div className="summary-items">
                  <div className="summary-item"><div className="item-icon cow">🐄</div><div className="item-details"><span className="item-label">Cow Milk</span><span className="item-value">{cowMorningQty.toFixed(1)} L</span><span className="item-amount">₹{totalAmount("Cow", "Morning").toFixed(2)}</span><span className="item-rate">@ ₹{cowMorningQty > 0 ? (totalAmount("Cow", "Morning") / cowMorningQty).toFixed(2) : '0'}/L</span></div></div>
                  <div className="summary-item"><div className="item-icon buffalo">🦬</div><div className="item-details"><span className="item-label">Buffalo Milk</span><span className="item-value">{buffaloMorningQty.toFixed(1)} L</span><span className="item-amount">₹{totalAmount("Buffalo", "Morning").toFixed(2)}</span><span className="item-rate">@ ₹{buffaloMorningQty > 0 ? (totalAmount("Buffalo", "Morning") / buffaloMorningQty).toFixed(2) : '0'}/L</span></div></div>
                </div>
                <div className="summary-total">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span>Total: {(cowMorningQty + buffaloMorningQty).toFixed(1)} L</span>
                    <span>₹{(totalAmount("Cow", "Morning") + totalAmount("Buffalo", "Morning")).toFixed(2)}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px', textAlign: 'right' }}>Avg: ₹{(cowMorningQty + buffaloMorningQty) > 0 ? ((totalAmount("Cow", "Morning") + totalAmount("Buffalo", "Morning")) / (cowMorningQty + buffaloMorningQty)).toFixed(2) : '0'}/L</div>
                </div>
              </div>
              <div className="summary-block evening">
                <div className="summary-block-header"><FaMoon className="time-icon" /><h3>Evening Collection</h3></div>
                <div className="summary-items">
                  <div className="summary-item"><div className="item-icon cow">🐄</div><div className="item-details"><span className="item-label">Cow Milk</span><span className="item-value">{cowEveningQty.toFixed(1)} L</span><span className="item-amount">₹{totalAmount("Cow", "Evening").toFixed(2)}</span><span className="item-rate">@ ₹{cowEveningQty > 0 ? (totalAmount("Cow", "Evening") / cowEveningQty).toFixed(2) : '0'}/L</span></div></div>
                  <div className="summary-item"><div className="item-icon buffalo">🦬</div><div className="item-details"><span className="item-label">Buffalo Milk</span><span className="item-value">{buffaloEveningQty.toFixed(1)} L</span><span className="item-amount">₹{totalAmount("Buffalo", "Evening").toFixed(2)}</span><span className="item-rate">@ ₹{buffaloEveningQty > 0 ? (totalAmount("Buffalo", "Evening") / buffaloEveningQty).toFixed(2) : '0'}/L</span></div></div>
                </div>
                <div className="summary-total">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span>Total: {(cowEveningQty + buffaloEveningQty).toFixed(1)} L</span>
                    <span>₹{(totalAmount("Cow", "Evening") + totalAmount("Buffalo", "Evening")).toFixed(2)}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px', textAlign: 'right' }}>Avg: ₹{(cowEveningQty + buffaloEveningQty) > 0 ? ((totalAmount("Cow", "Evening") + totalAmount("Buffalo", "Evening")) / (cowEveningQty + buffaloEveningQty)).toFixed(2) : '0'}/L</div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
              <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#333' }}>📊 Collection Insights</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Cow vs Buffalo</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#2ecc71' }}>{((cowMorningQty + cowEveningQty) / totalMilk * 100).toFixed(1)}% Cow</div>
                  <div style={{ fontSize: '14px', color: '#555' }}>{((buffaloMorningQty + buffaloEveningQty) / totalMilk * 100).toFixed(1)}% Buffalo</div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Morning vs Evening</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#2ecc71' }}>{((cowMorningQty + buffaloMorningQty) / totalMilk * 100).toFixed(1)}% Morning</div>
                  <div style={{ fontSize: '14px', color: '#555' }}>{((cowEveningQty + buffaloEveningQty) / totalMilk * 100).toFixed(1)}% Evening</div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Average Price</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#2ecc71' }}>₹{totalMilk > 0 ? (totalRevenue / totalMilk).toFixed(2) : '0'}/L</div>
                  <div style={{ fontSize: '12px', color: '#999' }}>Overall average</div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Entries Count</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#2ecc71' }}>{filtered.length}</div>
                  <div style={{ fontSize: '12px', color: '#999' }}>Total records</div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {!loading && (
        <div className="card">
          <div className="card-header-custom"><div className="card-title-icon"><FaUsers /></div><div><h2>Farmer-wise Summary</h2><p>{farmerSummary.length} farmers with milk entries</p></div></div>
          {farmerSummary.length === 0 ? (
            <div className="empty-state-enhanced"><div className="empty-icon">📊</div><h3>No Data Available</h3><p>No milk entries found for the selected date range</p></div>
          ) : (
            <div className="table-container">
              <table className="table enhanced-table">
                <thead><tr><th>Farmer</th><th>Total Qty</th><th>Total Amount</th><th>🐄 Cow</th><th>🦬 Buffalo</th><th>🌅 Morning</th><th>🌙 Evening</th></tr></thead>
                <tbody>
                  {farmerSummary.map((farmer, index) => (
                    <tr key={index} style={{ animationDelay: `${index * 0.03}s` }}>
                      <td><div className="farmer-cell"><div className="farmer-avatar-small">{farmer.name.charAt(0)}</div><div><strong>{farmer.name}</strong><span className="farmer-code-small">{farmer.code}</span></div></div></td>
                      <td><span className="highlight-value">{farmer.totalQuantity.toFixed(1)} L</span></td>
                      <td><span className="highlight-amount">₹{farmer.totalAmount.toFixed(2)}</span></td>
                      <td>{farmer.cowQuantity.toFixed(1)} L<br/><small>₹{farmer.cowAmount.toFixed(0)}</small></td>
                      <td>{farmer.buffaloQuantity.toFixed(1)} L<br/><small>₹{farmer.buffaloAmount.toFixed(0)}</small></td>
                      <td>{farmer.morningQuantity.toFixed(1)} L<br/><small>₹{farmer.morningAmount.toFixed(0)}</small></td>
                      <td>{farmer.eveningQuantity.toFixed(1)} L<br/><small>₹{farmer.eveningAmount.toFixed(0)}</small></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr><td><strong>Grand Total</strong></td><td><strong>{totalMilk.toFixed(1)} L</strong></td><td><strong>₹{totalRevenue.toFixed(2)}</strong></td><td><strong>{(cowMorningQty + cowEveningQty).toFixed(1)} L</strong></td><td><strong>{(buffaloMorningQty + buffaloEveningQty).toFixed(1)} L</strong></td><td><strong>{(cowMorningQty + buffaloMorningQty).toFixed(1)} L</strong></td><td><strong>{(cowEveningQty + buffaloEveningQty).toFixed(1)} L</strong></td></tr></tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
